import {
  AUTH_EXPIRED_STATUS,
  NEW_TOKEN_EXPIRED_STATUS,
  logoutAndGoLogin,
  newRequest,
  refreshNewToken,
} from "./complexRequest"
import { AuthorizationError } from "@/errors/AuthorizationError"
import { ComplexRequestOption, NewResponstype } from "./type"

export type SceneImageItem = {
  id?: number
  image_url: string
  trigger_desc: string
  emotion?: string
  image_source?: number
  sort?: number
}

export type SceneImageEmotion = {
  id: number
  code: string
  name: string
  default_trigger: string
  sort: number
}

export type SceneImageGenerateMode = "single" | "grid"

export type SceneImageGeneratePayload = {
  mode: SceneImageGenerateMode
  prompt?: string
  template_codes?: string[]
  reference_image: string
}

export type SceneImageStreamFrame = {
  type?: "meta" | "ping" | "image" | "done" | "error" | string
  mode?: string
  count?: number
  price?: number
  index?: number
  image_url?: string
  emotion?: string
  trigger_desc?: string
  code?: number
  status?: number
  message?: string
  msg?: string
}

export const GRID_SCENE_IMAGE_COUNT = 9
export const MAX_SCENE_IMAGES = 20
/** 触发条件上限，等于服务端 trigger_desc 列宽；超过会被服务端拒绝 */
export const MAX_SCENE_IMAGE_TRIGGER = 100
export const MAX_SCENE_IMAGE_PROMPT = 2000

/** 情绪模板库 GET /client/scene-image/templates */
export const getSceneImageTemplates = async (
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<NewResponstype<SceneImageEmotion[]>>({
    url: "/client/scene-image/templates",
    method: "get",
    ...option,
  }, useFetch)
}

/** 单图提示词润色 POST /client/scene-image/enhance */
export const enhanceSceneImagePrompt = async (
  data: { prompt: string },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<NewResponstype<{ prompt?: string } | string>>({
    url: "/client/scene-image/enhance",
    data,
    method: "post",
    ...option,
  }, useFetch)
}

export const pickSceneImagePrompt = (data?: { prompt?: string } | string | null) => {
  if (!data) return ""
  if (typeof data === "string") return data
  return data.prompt || ""
}

/** 接口 image.index 从 1 起，兼容 0 起的兜底 */
export const sceneImageDraftIndex = (index: unknown, length: number) => {
  const raw = Number(index)
  if (!Number.isFinite(raw) || length <= 0) return -1
  if (raw >= 1 && raw <= length) return raw - 1
  if (raw >= 0 && raw < length) return raw
  return -1
}

const extractJsonFrames = (buffer: string) => {
  const frames: string[] = []
  let start = -1
  let depth = 0
  let inString = false
  let escaped = false

  for (let i = 0; i < buffer.length; i++) {
    const ch = buffer[i]
    if (inString) {
      if (escaped) escaped = false
      else if (ch === "\\") escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') {
      inString = true
      continue
    }
    if (ch === "{") {
      if (depth === 0) start = i
      depth += 1
      continue
    }
    if (ch === "}") {
      depth -= 1
      if (depth === 0 && start >= 0) {
        frames.push(buffer.slice(start, i + 1))
        start = -1
      }
    }
  }

  return {
    frames,
    leftover: start >= 0 ? buffer.slice(start) : "",
  }
}

const emitFrame = (
  raw: string,
  onFrame: (frame: SceneImageStreamFrame, controller: AbortController) => void,
  controller: AbortController,
) => {
  const text = raw.trim()
  if (!text) return null
  const json = JSON.parse(text) as SceneImageStreamFrame
  onFrame(json, controller)
  return json
}

/** AI 生成情景图 POST /client/scene-image/generate，逐行 JSON 流 */
export const generateSceneImages = async (
  data: SceneImageGeneratePayload,
  { onFrame }: { onFrame: (frame: SceneImageStreamFrame, controller: AbortController) => void },
  isRetry = false,
) => {
  const controller = new AbortController()
  const response = await newRequest<any>({
    url: "/client/scene-image/generate",
    method: "post",
    data,
    signal: controller.signal,
    isStream: true,
  }, true)

  const reader = response.body.getReader()
  const decoder = new TextDecoder("utf-8")
  let timedOut = false
  let leftover = ""
  let last: SceneImageStreamFrame | null = null
  let authStatus: number | null = null

  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, 180_000)

  const takeStatus = (frame: SceneImageStreamFrame | null) => {
    const status = Number(frame?.status ?? frame?.code)
    if (status === AUTH_EXPIRED_STATUS || status === NEW_TOKEN_EXPIRED_STATUS) {
      authStatus = status
      controller.abort()
      return true
    }
    return false
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        const parsed = extractJsonFrames(leftover)
        leftover = parsed.leftover
        for (const item of parsed.frames) {
          try {
            last = emitFrame(item, onFrame, controller)
            takeStatus(last)
          } catch {
            // trailing incomplete chunk
          }
        }
        break
      }

      const parsed = extractJsonFrames(leftover + decoder.decode(value, { stream: true }))
      leftover = parsed.leftover
      for (const item of parsed.frames) {
        try {
          last = emitFrame(item, onFrame, controller)
          if (takeStatus(last)) return
        } catch {
          // skip malformed complete object
        }
      }
    }
  } catch {
    // aborted / network
  } finally {
    clearTimeout(timer)
  }

  if (authStatus === AUTH_EXPIRED_STATUS) {
    await logoutAndGoLogin()
    throw new AuthorizationError()
  }

  if (authStatus === NEW_TOKEN_EXPIRED_STATUS) {
    if (!isRetry) {
      const refreshed = await refreshNewToken()
      if (refreshed) {
        return generateSceneImages(data, { onFrame }, true)
      }
    }
    throw new AuthorizationError()
  }

  if (timedOut) throw new Error("timeout")
  return last
}

export const normalizeSceneImages = (raw: unknown): SceneImageItem[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (typeof item === "string") {
        return { image_url: item, trigger_desc: "", emotion: "" }
      }
      if (!item || typeof item !== "object") return null
      const record = item as { image_url?: string; url?: string; trigger_desc?: string; emotion?: string }
      const image_url = record.image_url || record.url || ""
      if (!image_url) return null
      return {
        image_url,
        trigger_desc: typeof record.trigger_desc === "string" ? record.trigger_desc : "",
        emotion: typeof record.emotion === "string" ? record.emotion : "",
      }
    })
    .filter((item): item is SceneImageItem => Boolean(item))
}
